/**
 * Web push in the browser (decision 0113): whether this browser can receive pushes, subscribing this device with the
 * Worker's VAPID key (`PushManager.subscribe` after `Notification.requestPermission`, both from a click), and the
 * /api/me/push routes. iPhone and iPad (iOS 16.4+) receive pushes only in the installed app, never in a Safari tab.
 */
import { PushPrefs, PushStatus, PushTestResult } from "@minga/protocol"
import { queryOptions } from "@tanstack/react-query"
import { Schema } from "effect"
import { apiJson, apiRequest } from "./api"
import { currentPlatform, isStandalone } from "./pwa"

export const pushStatusQuery = queryOptions({
  queryKey: ["me", "push"],
  queryFn: () => apiJson("/api/me/push", PushStatus),
  staleTime: 60_000,
})

export const savePushPrefs = (prefs: PushPrefs) =>
  apiJson("/api/me/push/prefs", PushPrefs, { method: "PUT", body: prefs })

export const removePushDevice = (id: string) =>
  apiRequest(`/api/me/push/devices/${encodeURIComponent(id)}`, null, { method: "DELETE" })

export const sendTestPush = () => apiJson("/api/me/push/test", PushTestResult, { method: "POST" })

/**
 * - `supported`: this browser can subscribe now.
 * - `needs_install`: iPhone/iPad in a Safari tab; pushes work once Minga is on the Home Screen (iOS 16.4+).
 * - `unsupported`: no service worker, Push API or Notification API here.
 */
export type PushSupport = "supported" | "needs_install" | "unsupported"

export const pushSupportOf = (env: {
  readonly ios: boolean
  readonly standalone: boolean
  readonly serviceWorker: boolean
  readonly pushManager: boolean
  readonly notification: boolean
}): PushSupport => {
  if (env.ios && !env.standalone) return "needs_install"
  return env.serviceWorker && env.pushManager && env.notification ? "supported" : "unsupported"
}

export const currentPushSupport = (): PushSupport =>
  typeof window === "undefined"
    ? "unsupported"
    : pushSupportOf({
        ios: currentPlatform().ios,
        standalone: isStandalone(),
        serviceWorker: "serviceWorker" in navigator,
        pushManager: "PushManager" in window,
        notification: "Notification" in window,
      })

/** `default` (not asked yet), `granted` or `denied`; `default` where the API is missing. */
export const notificationPermission = (): NotificationPermission =>
  typeof Notification === "undefined" ? "default" : Notification.permission

export const keyBytes = (base64url: string): Uint8Array<ArrayBuffer> => {
  const b64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
  const raw = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4))
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

const sameKey = (a: ArrayBuffer | null | undefined, b: Uint8Array): boolean => {
  if (!a || a.byteLength !== b.byteLength) return false
  const view = new Uint8Array(a)
  return view.every((x, i) => x === b[i])
}

/** The registration of the app's service worker (none in `vite dev`: push needs a build). */
const registration = async (): Promise<ServiceWorkerRegistration | null> =>
  "serviceWorker" in navigator ? ((await navigator.serviceWorker.getRegistration()) ?? null) : null

/** This browser's push subscription, if it has one. */
export const currentSubscription = async (): Promise<PushSubscription | null> => {
  const reg = await registration()
  return reg ? reg.pushManager.getSubscription() : null
}

const register = (sub: PushSubscription) => {
  const json = sub.toJSON()
  return apiJson("/api/me/push/subscription", Schema.Struct({ id: Schema.String }), {
    method: "PUT",
    body: { endpoint: json.endpoint, keys: json.keys },
  })
}

export class PushSetupError extends Error {
  constructor(
    readonly reason: "denied" | "no_worker" | "unsupported",
    message: string,
  ) {
    super(message)
  }
}

/**
 * "Allow notifications": ask for permission (must run from a click), subscribe with the Worker's key (replacing a
 * subscription made with an older key) and register the device. Answers the device id.
 */
export const enablePush = async (publicKey: string): Promise<string> => {
  if (currentPushSupport() !== "supported")
    throw new PushSetupError("unsupported", "This browser can't receive push notifications.")
  const permission = await Notification.requestPermission()
  if (permission !== "granted")
    throw new PushSetupError(
      "denied",
      permission === "denied"
        ? "Notifications are blocked for Minga. Allow them in this browser's site settings, then try again."
        : "Notifications weren't allowed.",
    )
  const reg = await registration()
  if (!reg)
    throw new PushSetupError("no_worker", "The app isn't ready for notifications yet. Reload and try again.")
  const key = keyBytes(publicKey)
  let sub = await reg.pushManager.getSubscription()
  if (sub && !sameKey(sub.options.applicationServerKey, key)) {
    await sub.unsubscribe().catch(() => false)
    sub = null
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  return (await register(sub)).id
}

/** "Turn off on this device": unsubscribe the browser and forget the device on the server. */
export const disablePush = async (): Promise<void> => {
  const sub = await currentSubscription()
  if (!sub) return
  const endpoint = sub.endpoint
  await sub.unsubscribe().catch(() => false)
  await apiRequest("/api/me/push/subscription", null, { method: "DELETE", body: { endpoint } })
}

/**
 * On opening the settings: a subscription this browser holds is (re)registered with the server, so a device removed
 * elsewhere or made under an old key heals; one under another key is replaced. Answers whether this device is on.
 */
export const syncPush = async (publicKey: string): Promise<boolean> => {
  if (notificationPermission() !== "granted") return false
  const sub = await currentSubscription()
  if (!sub) return false
  if (!sameKey(sub.options.applicationServerKey, keyBytes(publicKey))) {
    await enablePush(publicKey)
    return true
  }
  await register(sub)
  return true
}
